import { getOrderDetails } from '@app/ducks';
import { useDownloadOrderExcel } from '@app/services/hooks/documents/useDownloadOrderExcel';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useAmplitude } from '@app/utils/hooks';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DownloadLinesheetModal } from '../orders/DownloadLinesheetModal';
import { DownloadProductInformationModal } from '../orders/DownloadProductInformationModal';
import { Button } from '../various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '../various/Dropdown';
import { Download } from '../various/Icons';

export const DownloadActions = () => {
  const { t } = useTranslation(['checkout', 'common']);
  const { order } = useSelector(getOrderDetails);
  const { trackEvent } = useAmplitude();

  const isProductInfoConfiguratorEnabled = getIsFeatureEnabled('ENABLE_PRODUCT_INFO_FILE_CONFIG');
  const { showModal: showLinesheetModal, hideModal: hideLinesheetModal, isOpen: isLinesheetModalOpen } = useModalToggle();
  const { showModal: showProductInfoModal, hideModal: hideProductInfoModal, isOpen: isProductInfoModalOpen } = useModalToggle();
  const { mutate: downloadOrderExcel } = useDownloadOrderExcel();

  const handleSuccessDownloadLinesheetModal = () => {
    trackEvent({ name: 'downloadAction.button.linesheet.submit', properties: { path: 'allProducts' } });
  };

  const handleSuccessProductInfoModal = () => {
    trackEvent({ name: 'downloadAction.button.productInformationXls.submit', properties: { path: 'allProducts' } });
  };

  const handleDownloadOrderExcel = React.useCallback(() => {
    downloadOrderExcel(
      {
        orderId: order.order,
        orderNumber: order.orderNumber,
      },
      {
        onSuccess: () => {
          trackEvent({ name: 'downloadAction.button.orderDetailsXls.submit', properties: { path: 'allProducts' } });
        },
      },
    );
  }, [order, downloadOrderExcel, trackEvent]);

  const downloadActions = [
    {
      key: 'downloadOrderDetailsXls',
      label: t('checkout:download.order_details', { extension: 'xls' }),
      onClick: handleDownloadOrderExcel,
    },
    {
      key: 'downloadLinesheetPdf',
      label: t('checkout:download.linesheet', { extension: 'pdf' }),
      onClick: showLinesheetModal,
    },
    ...(isProductInfoConfiguratorEnabled ?
      [
        {
          key: 'productInfoXls',
          label: t('checkout:download.product_info', { extension: 'xls' }),
          onClick: showProductInfoModal,
        },
      ]
    : []),
  ];

  return (
    <>
      <Dropdown title={t('common:download')}>
        <DropdownTrigger>
          {({ isOpen, triggerProps }) => (
            <Button {...triggerProps} icon={Download} color="secondary" variant="bordered" isChevronOpen={isOpen} size="medium" />
          )}
        </DropdownTrigger>
        <DropdownMenu items={downloadActions} />
      </Dropdown>

      <DownloadLinesheetModal
        orderId={order.order}
        orderNumber={order.orderNumber}
        location="select units"
        downloadOnlySelected={false}
        isOpen={isLinesheetModalOpen}
        onClose={hideLinesheetModal}
        onSuccess={handleSuccessDownloadLinesheetModal}
      />

      {isProductInfoConfiguratorEnabled && (
        <DownloadProductInformationModal
          isOpen={isProductInfoModalOpen}
          onClose={hideProductInfoModal}
          onSuccess={handleSuccessProductInfoModal}
        />
      )}
    </>
  );
};
